<!-- App.vue -->
<template>
  <div id="app">
    <!-- 未登录显示登录页 -->
    <Login v-if="!isLoggedIn" />
    <!-- 已登录显示主界面 -->
    <div v-else class="app-main">
      <AppHeader />
      <div class="app-body">
        <!-- 全局 Loading -->
        <div v-if="loadingStore.showLoading" class="global-loading-container">
          <div class="global-loading-content">
            <div class="global-spinner" />
            <span>加载中...</span>
          </div>
        </div>
        <router-view v-slot="{ Component }">
          <keep-alive :include="keepAliveViews">
            <component :is="Component" />
          </keep-alive>
        </router-view>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAppStore, useCustomerStore, useStyleStore, useLoadingStore } from '@/stores'
import AppHeader from '@/components/common/AppHeader.vue'
import Login from '@/views/Login.vue'

const appStore = useAppStore()
const customerStore = useCustomerStore()
const styleStore = useStyleStore()
const loadingStore = useLoadingStore()
const router = useRouter()
const route = useRoute()

const isLoggedIn = computed(() => appStore.isLoggedIn)

const keepAliveViews = computed(() => {
  const tabs = appStore.moduleTabs
  return tabs.filter(t => t.keepAlive).map(t => t.componentName)
})

onMounted(async () => {
  // ✅ 记录进入应用时的原始路径（在 checkLoginStatus 之前，此时 route 还没被守卫改过）
  const intendedPath = (route.path !== '/login' && route.path !== '/')
    ? route.fullPath
    : ''

  await appStore.checkLoginStatus()

  if (appStore.isLoggedIn) {
    await customerStore.loadCustomer()
    await styleStore.loadGlobalStyles()

    if (router.currentRoute.value.path === '/login') {
      // ✅ 优先级：query.redirect > 原始路径 > /production
      const queryRedirect = router.currentRoute.value.query.redirect || ''
      const target = queryRedirect || intendedPath || '/production'
      router.replace(target)
    }
  } else {
    if (router.currentRoute.value.path !== '/login') {
      router.replace({ path: '/login', query: { redirect: route.fullPath } })
    }
  }

  window.addEventListener('beforeunload', handleBeforeUnload)
})

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
})

function handleBeforeUnload() {
  // 保留你原有的实现；如果原来没做任何事，留空即可
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html,
body,
#app {
  height: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  background: #f5f7fa;
  font-size: 14px;
}

.app-main {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

.app-body {
  flex: 1;
  overflow: hidden;
  padding: 12px 16px;
  position: relative;
}

/* Varlet 全局主题覆盖 */
:root {
  --var-primary-color: #409eff;
  --var-success-color: #67c23a;
  --var-danger-color: #f56c6c;
  --var-warning-color: #e6a23c;
}

/* ===== 全局 Loading ===== */
.global-loading-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
  z-index: 9999;
  backdrop-filter: blur(2px);
  border-radius: 8px;
}

.global-loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 32px 48px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.global-spinner {
  width: 44px;
  height: 44px;
  border: 4px solid #f0f0f0;
  border-top-color: #2979ff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* 移动端适配 */
@media (max-width: 768px) {
  .app-body {
    padding: 0;
  }

  .global-loading-content {
    padding: 24px 32px;
  }

  .global-spinner {
    width: 36px;
    height: 36px;
  }
}
</style>